import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarDays, List, Plus, ChevronLeft, ChevronRight, Clock, User,
  Phone, Wrench, MapPin, FileText, XCircle, ArrowRight, Search, Filter
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";

const STATUS_COLORS = {
  "Scheduled": { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]", dot: "bg-[#3B82F6]" },
  "Job Card Created": { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", dot: "bg-[#4CAF50]" },
  "Completed": { bg: "bg-[#E8F5E9]", text: "text-[#2E7D32]", dot: "bg-[#4CAF50]" },
  "Cancelled": { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", dot: "bg-[#EF4444]" },
  "No-Show": { bg: "bg-[#FFF7ED]", text: "text-[#EA580C]", dot: "bg-[#F97316]" },
};

const LOCATIONS = {
  loc1: "Location 1",
  loc2: "Location 2",
};

const BookingsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") === "calendar" ? "calendar" : "list";
  const [view, setView] = useState(initialView);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [converting, setConverting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  // Calendar state
  const [calWeekStart, setCalWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  });

  const fetchBookings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bookings`);
      setBookings(r.data);
    } catch (err) { toast.error("Failed to fetch bookings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    setSearchParams(view === "calendar" ? { view: "calendar" } : {}, { replace: true });
  }, [view, setSearchParams]);

  const filteredBookings = useMemo(() => {
    let result = bookings;
    if (statusFilter !== "all") result = result.filter(b => b.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(b =>
        b.customer_name.toLowerCase().includes(q) ||
        b.booking_number.toLowerCase().includes(q) ||
        b.mobile_number.includes(q) ||
        b.service_type.toLowerCase().includes(q)
      );
    }
    return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [bookings, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: bookings.length, Scheduled: 0, "Job Card Created": 0, Cancelled: 0, Completed: 0, "No-Show": 0 };
    bookings.forEach(b => { if (c[b.status] !== undefined) c[b.status]++; });
    return c;
  }, [bookings]);

  const handleConvert = async (booking) => {
    if (!window.confirm(`Convert ${booking.booking_number} to a Job Card?`)) return;
    setConverting(booking.id);
    try {
      const r = await axios.post(`${API}/bookings/${booking.id}/convert-to-jobcard`);
      toast.success(r.data.message);
      fetchBookings();
      if (detailOpen) setDetailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert booking");
    } finally { setConverting(null); }
  };

  const handleCancel = async (booking) => {
    setCancelBooking(booking);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!cancelBooking) return;
    setCancelling(cancelBooking.id);
    try {
      await axios.put(`${API}/bookings/${cancelBooking.id}`, { status: "Cancelled", notes: cancelBooking.notes ? `${cancelBooking.notes}\nCancellation reason: ${cancelReason}` : `Cancellation reason: ${cancelReason}` });
      toast.success(`Booking ${cancelBooking.booking_number} cancelled`);
      fetchBookings();
      if (detailOpen) setDetailOpen(false);
      setCancelDialogOpen(false);
      setCancelBooking(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel booking");
    } finally { setCancelling(null); }
  };

  const openDetail = (booking) => {
    setDetailBooking(booking);
    setDetailOpen(true);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  };

  // Calendar helpers
  const calDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(calWeekStart);
      d.setDate(calWeekStart.getDate() + i);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      days.push({
        date: d,
        dateStr: d.toISOString().split("T")[0],
        dayName: d.toLocaleDateString("en-US", { weekday: "short" }),
        dateLabel: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        isToday: d.getTime() === today.getTime(),
        isPast: d < today,
      });
    }
    return days;
  }, [calWeekStart]);

  const calWeekLabel = `${calDays[0]?.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${calDays[6]?.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  const bookingsByDate = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    });
    return map;
  }, [bookings]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[22px] font-bold text-[#1E293B]">Bookings</h1>
          <p className="text-[13px] text-[#94A3B8]">Manage all workshop bookings</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
            <button data-testid="view-list-btn" onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${view === "list" ? "bg-white text-[#1E293B] shadow-sm" : "text-[#64748B]"}`}>
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button data-testid="view-calendar-btn" onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${view === "calendar" ? "bg-white text-[#1E293B] shadow-sm" : "text-[#64748B]"}`}>
              <CalendarDays className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>
          <Button data-testid="new-booking-btn" onClick={() => navigate("/bookings/new")}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-semibold gap-1.5 h-9 rounded-lg">
            <Plus className="w-4 h-4" /> New Booking
          </Button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "all", label: "All", count: counts.all },
          { key: "Scheduled", label: "Scheduled", count: counts.Scheduled },
          { key: "Job Card Created", label: "Job Card Created", count: counts["Job Card Created"] },
          { key: "Cancelled", label: "Cancelled", count: counts.Cancelled },
        ].map(tab => (
          <button key={tab.key} data-testid={`filter-${tab.key}`} onClick={() => setStatusFilter(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-all ${
              statusFilter === tab.key
                ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#3B82F6]"
            }`}>
            {tab.label} <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${statusFilter === tab.key ? "bg-white/20" : "bg-[#F1F5F9]"}`}>{tab.count}</span>
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
          <Input data-testid="booking-search" placeholder="Search bookings..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 w-64 text-[12px] bg-[#F8FAFC] border-[#E2E8F0]" />
        </div>
      </div>

      {view === "list" ? (
        /* ==================== LIST VIEW ==================== */
        <div className="bg-white rounded-2xl shadow-sm border border-[#F1F5F9] overflow-hidden">
          {loading ? (
            <p className="text-center py-12 text-[13px] text-[#94A3B8]">Loading bookings...</p>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-16">
              <CalendarDays className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
              <p className="text-[#94A3B8] text-[14px]">No bookings found</p>
              <Button onClick={() => navigate("/bookings/new")} className="mt-3 bg-[#3B82F6] text-white text-[12px] gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Create First Booking
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#F1F5F9] bg-[#FAFBFC]">
                  {["BOOKING #", "CUSTOMER", "CONTACT", "SERVICE", "DATE", "TIME SLOTS", "STATUS", "ACTIONS"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(booking => {
                  const sc = STATUS_COLORS[booking.status] || STATUS_COLORS["Scheduled"];
                  return (
                    <tr key={booking.id} data-testid={`booking-row-${booking.id}`}
                      className="border-b border-[#F1F5F9] hover:bg-[#FAFBFC] transition-colors">
                      <td className="px-4 py-3">
                        <button data-testid={`booking-link-${booking.id}`} onClick={() => openDetail(booking)}
                          className="text-[13px] font-semibold text-[#3B82F6] hover:underline">{booking.booking_number}</button>
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#1E293B] font-medium">{booking.customer_name}</td>
                      <td className="px-4 py-3 text-[12px] text-[#64748B]">{booking.mobile_number}</td>
                      <td className="px-4 py-3 text-[12px] text-[#475569]">{booking.service_type}</td>
                      <td className="px-4 py-3 text-[12px] text-[#475569]">{formatDate(booking.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(booking.time_slots || []).map(s => (
                            <span key={s} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#3B82F6]">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} /> {booking.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {booking.status === "Scheduled" && (
                            <>
                              <Button data-testid={`convert-btn-${booking.id}`} size="sm" onClick={() => handleConvert(booking)}
                                disabled={converting === booking.id}
                                className="bg-[#16A34A] hover:bg-[#15803D] text-white text-[10px] h-7 px-2 gap-1 rounded-md">
                                <FileText className="w-3 h-3" /> {converting === booking.id ? "..." : "To Job Card"}
                              </Button>
                              <Button data-testid={`cancel-btn-${booking.id}`} size="sm" variant="outline" onClick={() => handleCancel(booking)}
                                disabled={cancelling === booking.id}
                                className="text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2] text-[10px] h-7 px-2 gap-1 rounded-md">
                                <XCircle className="w-3 h-3" /> {cancelling === booking.id ? "..." : "Cancel"}
                              </Button>
                            </>
                          )}
                          {booking.status === "Job Card Created" && booking.jobcard_id && (
                            <Button data-testid={`view-jc-btn-${booking.id}`} size="sm" variant="outline"
                              onClick={() => navigate(`/job-cards?highlight=${booking.job_card_number}`)}
                              className="text-[11px] text-[#3B82F6] border-[#BFDBFE] hover:bg-[#EFF6FF] h-7 px-2.5 gap-1 rounded-md font-semibold">
                              <FileText className="w-3 h-3" /> View {booking.job_card_number || "Job Card"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        /* ==================== CALENDAR VIEW ==================== */
        <div className="bg-white rounded-2xl shadow-sm border border-[#F1F5F9] p-5">
          {/* Week nav */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[#64748B]" />
              <span className="text-[14px] font-semibold text-[#1E293B]">{calWeekLabel}</span>
              <button data-testid="cal-prev" onClick={() => { const d = new Date(calWeekStart); d.setDate(d.getDate() - 7); setCalWeekStart(d); }}
                className="p-1 rounded hover:bg-[#F1F5F9]"><ChevronLeft className="w-4 h-4 text-[#64748B]" /></button>
              <button data-testid="cal-next" onClick={() => { const d = new Date(calWeekStart); d.setDate(d.getDate() + 7); setCalWeekStart(d); }}
                className="p-1 rounded hover:bg-[#F1F5F9]"><ChevronRight className="w-4 h-4 text-[#64748B]" /></button>
              <button onClick={() => { const d = new Date(); d.setHours(0,0,0,0); const day = d.getDay(); d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); setCalWeekStart(d); }}
                className="ml-2 text-[11px] text-[#3B82F6] font-medium px-2 py-1 rounded-md hover:bg-[#EFF6FF]">Today</button>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Scheduled</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4CAF50]" /> Job Card Created</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EF4444]" /> Cancelled</span>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-3">
            {calDays.map((day) => {
              const dayBookings = bookingsByDate[day.dateStr] || [];
              const activeBookings = dayBookings.filter(b => b.status !== "Cancelled");
              return (
                <div key={day.dateStr} data-testid={`cal-day-${day.dateStr}`}
                  className={`rounded-xl border min-h-[200px] ${day.isToday ? "border-[#3B82F6] bg-[#FAFBFF]" : day.isPast ? "border-[#E2E8F0] bg-[#FAFAFA] opacity-60" : "border-[#E2E8F0]"}`}>
                  {/* Day header */}
                  <div className={`px-3 py-2 border-b ${day.isToday ? "border-[#DBEAFE] bg-[#EFF6FF]" : "border-[#F1F5F9]"} rounded-t-xl`}>
                    <p className={`text-[11px] font-semibold ${day.isToday ? "text-[#3B82F6]" : "text-[#94A3B8]"}`}>{day.dayName}</p>
                    <p className={`text-[15px] font-bold ${day.isToday ? "text-[#1D4ED8]" : "text-[#1E293B]"}`}>{day.dateLabel}</p>
                    {activeBookings.length > 0 && (
                      <p className="text-[10px] text-[#64748B] mt-0.5">{activeBookings.length} booking{activeBookings.length > 1 ? "s" : ""}</p>
                    )}
                  </div>
                  {/* Bookings in day */}
                  <div className="p-2 space-y-1.5 max-h-[300px] overflow-y-auto">
                    {dayBookings.length === 0 ? (
                      <p className="text-[10px] text-[#CBD5E1] text-center py-4">No bookings</p>
                    ) : (
                      dayBookings.map(b => {
                        const sc = STATUS_COLORS[b.status] || STATUS_COLORS["Scheduled"];
                        return (
                          <button key={b.id} data-testid={`cal-booking-${b.id}`} onClick={() => openDetail(b)}
                            className={`w-full text-left rounded-lg p-2 border transition-all hover:shadow-sm ${
                              b.status === "Cancelled" ? "border-[#FECACA] bg-[#FEF2F2] opacity-60" :
                              b.status === "Job Card Created" ? "border-[#BBF7D0] bg-[#F0FDF4]" :
                              "border-[#DBEAFE] bg-[#EFF6FF] hover:border-[#93C5FD]"
                            }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold text-[#3B82F6]">{b.booking_number}</span>
                              <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
                            </div>
                            <p className="text-[11px] font-medium text-[#1E293B] truncate">{b.customer_name}</p>
                            <p className="text-[9px] text-[#64748B] mt-0.5">{b.service_type}</p>
                            {b.status === "Job Card Created" && b.job_card_number && (
                              <p className="text-[9px] font-semibold text-[#16A34A] mt-0.5">{b.job_card_number}</p>
                            )}
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {(b.time_slots || []).slice(0, 2).map(s => (
                                <span key={s} className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-white/70 text-[#475569]">{s}</span>
                              ))}
                              {(b.time_slots || []).length > 2 && (
                                <span className="text-[8px] text-[#94A3B8]">+{b.time_slots.length - 2}</span>
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[16px] flex items-center gap-2">
              {detailBooking?.booking_number}
              {detailBooking && (() => {
                const sc = STATUS_COLORS[detailBooking.status] || STATUS_COLORS["Scheduled"];
                return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>{detailBooking.status}</span>;
              })()}
            </DialogTitle>
            <DialogDescription>Booking Details</DialogDescription>
          </DialogHeader>
          {detailBooking && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <DetailField icon={User} label="Customer" value={detailBooking.customer_name} />
                <DetailField icon={Phone} label="Mobile" value={detailBooking.mobile_number} />
                <DetailField icon={Wrench} label="Service" value={detailBooking.service_type} />
                <DetailField icon={MapPin} label="Location" value={LOCATIONS[detailBooking.location] || detailBooking.location} />
                <DetailField icon={CalendarDays} label="Date" value={formatDate(detailBooking.date)} />
                <DetailField icon={Clock} label="Time Slots" value={(detailBooking.time_slots || []).join(", ")} />
              </div>
              {detailBooking.notes && (
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-[10px] text-[#94A3B8] mb-1">Notes</p>
                  <p className="text-[12px] text-[#475569]">{detailBooking.notes}</p>
                </div>
              )}
              <div className="text-[10px] text-[#94A3B8]">
                Source: {detailBooking.booking_source} | Created: {new Date(detailBooking.created_at).toLocaleString("en-IN")}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-[#F1F5F9]">
                {detailBooking.status === "Scheduled" && (
                  <>
                    <Button data-testid="modal-convert-btn" onClick={() => handleConvert(detailBooking)}
                      disabled={converting === detailBooking.id}
                      className="flex-1 bg-[#16A34A] hover:bg-[#15803D] text-white text-[12px] gap-1.5 font-semibold">
                      <FileText className="w-3.5 h-3.5" /> {converting === detailBooking.id ? "Converting..." : "Convert to Job Card"}
                    </Button>
                    <Button data-testid="modal-cancel-btn" variant="outline" onClick={() => handleCancel(detailBooking)}
                      disabled={cancelling === detailBooking.id}
                      className="text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2] text-[12px] gap-1.5 font-semibold">
                      <XCircle className="w-3.5 h-3.5" /> {cancelling === detailBooking.id ? "..." : "Cancel Booking"}
                    </Button>
                  </>
                )}
                {detailBooking.status === "Job Card Created" && detailBooking.jobcard_id && (
                  <Button onClick={() => { setDetailOpen(false); navigate(`/job-cards?highlight=${detailBooking.job_card_number}`); }}
                    className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> View {detailBooking.job_card_number || "Job Card"}
                  </Button>
                )}
                {detailBooking.status === "Cancelled" && (
                  <p className="text-[12px] text-[#94A3B8] italic">This booking has been cancelled.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Cancel Booking Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-[16px] text-[#DC2626]">Cancel Booking</DialogTitle>
            <DialogDescription>
              Cancel {cancelBooking?.booking_number}? This will free up the time slots.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[12px] font-medium text-[#475569] mb-1 block">Cancellation Reason</label>
              <textarea data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] resize-none"
                rows={3} placeholder="Please provide a reason for cancellation..." />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="flex-1 text-[12px]">Keep Booking</Button>
              <Button data-testid="confirm-cancel-btn" onClick={confirmCancel} disabled={cancelling === cancelBooking?.id}
                className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12px] font-semibold">
                {cancelling === cancelBooking?.id ? "Cancelling..." : "Confirm Cancel"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailField = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-3.5 h-3.5 text-[#94A3B8] mt-0.5 shrink-0" />
    <div>
      <p className="text-[10px] text-[#94A3B8]">{label}</p>
      <p className="text-[12px] font-medium text-[#1E293B]">{value || "-"}</p>
    </div>
  </div>
);

export default BookingsListPage;
